import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CompanySettings {
  summerScheduleActive: boolean;
  summerDailyMinutes: number;
}

export function AdminSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api<CompanySettings>('/admin/settings'),
  });

  const [active, setActive] = useState(false);
  const [dailyMinutes, setDailyMinutes] = useState(450);
  const [minutesDirty, setMinutesDirty] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);

  useEffect(() => {
    if (settings.data) {
      setActive(settings.data.summerScheduleActive);
      if (!minutesDirty) setDailyMinutes(settings.data.summerDailyMinutes);
    }
  }, [settings.data, minutesDirty]);

  const update = useMutation({
    mutationFn: (next: Partial<CompanySettings>) =>
      api<CompanySettings>('/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(next),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'settings'], data);
      setSavedFlag(true);
      window.setTimeout(() => setSavedFlag(false), 2000);
    },
  });

  function onToggleActive(next: boolean) {
    setActive(next);
    update.mutate({ summerScheduleActive: next });
  }

  function onSaveMinutes() {
    const v = Math.trunc(dailyMinutes);
    if (!Number.isFinite(v) || v < 0 || v > 24 * 60) return;
    setMinutesDirty(false);
    update.mutate({ summerDailyMinutes: v });
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight2">Ajustes de empresa</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Configuración global aplicable a todos los empleados.
        </p>
      </header>

      <div className="space-y-5">
        <Panel
          title="Jornada de verano"
          description="Reduce las horas esperadas por día durante la temporada de verano. Solo afecta a los empleados a jornada completa; quienes están a media jornada conservan siempre su horario. El cambio se aplica a partir del próximo recálculo de jornada."
        >
          {settings.isLoading ? (
            <p className="px-6 py-5 text-sm text-muted-foreground">Cargando…</p>
          ) : settings.isError ? (
            <p role="alert" className="px-6 py-5 text-sm text-destructive">
              {settings.error instanceof ApiError
                ? settings.error.message
                : 'Error al cargar ajustes'}
            </p>
          ) : (
            <>
              <SettingRow
                title="Activar horario de verano"
                description="Si lo activas, los empleados a jornada completa usan los minutos diarios definidos abajo en lugar de los de su horario normal. Los empleados a media jornada mantienen siempre su horario y no se ven afectados."
                action={
                  <Toggle
                    checked={active}
                    disabled={update.isPending}
                    onChange={onToggleActive}
                    ariaLabel="Activar horario de verano"
                  />
                }
              />
              <div className="border-t border-border" />
              <div className="px-6 py-5">
                <Label htmlFor="summer-minutes" className="text-[14px] font-medium">
                  Minutos diarios en verano
                </Label>
                <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                  Cuánto trabaja cada empleado al día cuando el horario de verano está activo. Por
                  defecto 450 minutos (7h30). Rango admitido: 0–1440.
                </p>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <Input
                    id="summer-minutes"
                    type="number"
                    min={0}
                    max={1440}
                    step={15}
                    value={dailyMinutes}
                    onChange={(e) => {
                      setMinutesDirty(true);
                      setDailyMinutes(Number(e.target.value));
                    }}
                    className="w-32"
                  />
                  <span className="text-[13px] text-muted-foreground tabular-nums">
                    ≈ {formatHm(dailyMinutes)}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onSaveMinutes}
                    disabled={!minutesDirty || update.isPending}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
              {update.error ? (
                <div className="px-6 py-3 border-t border-border bg-destructive/5">
                  <p role="alert" className="text-sm text-destructive">
                    {update.error instanceof ApiError ? update.error.message : 'Error al guardar'}
                  </p>
                </div>
              ) : savedFlag ? (
                <div className="px-6 py-3 border-t border-border bg-success/5">
                  <p className="text-[13px] text-success font-medium">Guardado.</p>
                </div>
              ) : null}
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}

function formatHm(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.trunc(minutes % 60);
  return `${h}h${m.toString().padStart(2, '0')}`;
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="px-6 py-4 border-b border-border">
        <h2 className="text-base font-semibold tracking-tightish">{title}</h2>
        {description ? (
          <p className="text-[13px] text-muted-foreground mt-0.5">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function SettingRow({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action: React.ReactNode;
}) {
  return (
    <div className="px-6 py-5 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-foreground">{title}</p>
        {description ? (
          <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{description}</p>
        ) : null}
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ backgroundColor: checked ? '#1B23B8' : '#D9D4C5' }}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white transition-transform shadow-sm"
        style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }}
      />
    </button>
  );
}
